import { inject, Injectable } from '@angular/core';
import { Http, QueryHandlerFor, RootContext } from '@skylab-angular-shared/core';
import { OrderDirection, PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO, UnitQuery, withModule } from '@skylab-shared/business';
import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { ProductKind } from '@skylab-shared/dataModel';

export type ProductQLCollectionQueryParams = {
    currencyId: number;
    name?: string;
    kind?: ProductKind;
    additionalCurrencyId?: number;
    inventoryControl: boolean;
};

export type ProductQLCollectionItem = {
    favourite: boolean;
    readonly id: number;
    readonly externalId: string;
    version: string;
    readonly name: string;
    readonly inventoryLevel: string;
    readonly inventoryControl: boolean;
    readonly unitSymbol: string;
    readonly netPrice: number;
    readonly grossPrice: number;
    readonly purchaseNetPrice: number;
    readonly purchaseGrossPrice: number;
    readonly netPriceCurrency?: number;
    readonly grossPriceCurrency?: number;
    readonly symbol?: string;
};

class ProductQLCollectionItemDTO {
    readonly favourite: boolean;
    readonly id: number;
    readonly externalId: string;
    readonly version: string;
    readonly kind: ProductKind;
    readonly name: string;
    readonly unitId: number;
    readonly inventoryLevel?: number;
    readonly inventoryControl: boolean;
    readonly netPrice: number;
    readonly grossPrice: number;
    readonly netPriceCurrency?: number;
    readonly grossPriceCurrency?: number;
    readonly purchaseNetPrice: number;
    readonly purchaseGrossPrice: number;
    readonly currencyId: number;
    readonly additionalCurrencyId: number;
    readonly symbol?: string;
}

export class ProductQLCollectionQuery extends PagedCollectionQuery<ProductQLCollectionItem> { }

@Injectable({ providedIn: 'root'})
@QueryHandlerFor(ProductQLCollectionQuery)
export class ProductQLCollectionQueryHandler implements QueryHandler<ProductQLCollectionQuery, PagedCollectionQueryResult<ProductQLCollectionItem>> {
    #Url = TaggedUrl.join('products/quickList', [Tags.Api, Tags.NotHandle404]);
    #http =  inject(Http) as HttpService;
    #rootContext =  inject(RootContext) as any as RootContextBase;

    async handleAsync(query: ProductQLCollectionQuery, params: ProductQLCollectionQueryParams): Promise<PagedCollectionQueryResult<ProductQLCollectionItem>> {
        const { data } = await this.#http.get(this.#Url, {
            params: {
                ...query.params,
                ...params,
                includeInventoryLevels: withModule('Inventory'),
                orderBy: [`favourite+${OrderDirection.Desc}`, `name+${OrderDirection.Asc}`],
            },
            dtoObject: new PagedCollectionQueryResultDTO<ProductQLCollectionItemDTO>(ProductQLCollectionItemDTO)
        });

        const items = await Promise.all(data.items.map<Promise<ProductQLCollectionItem>>(async item => {
            const unit = await this.#rootContext.firstAsync(UnitQuery, { id: item.unitId });

            const mappedItem: ProductQLCollectionItem = {
                favourite: item.favourite,
                id: item.id,
                unitSymbol: unit.symbol,
                version: item.version,
                name: item.name,
                inventoryControl: item.inventoryControl,
                inventoryLevel: item.inventoryLevel?.toString()?.replace('.', ',') || '',
                grossPrice: item.grossPrice,
                netPrice: item.netPrice,
                purchaseNetPrice: item.purchaseNetPrice,
                purchaseGrossPrice: item.purchaseGrossPrice,
                netPriceCurrency: item.netPriceCurrency,
                grossPriceCurrency: item.grossPriceCurrency,
                symbol: item.symbol,
                externalId: item.externalId
            };

            return mappedItem;
        }));

        return {
            totalItemsCount: data.totalItemsCount,
            items
        };
    }
}
